import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmtINR, fmtUSD, fmtQty, isUSExchange } from "@/lib/format";
import { usePortfolio } from "@/context/PortfolioContext";
import { Plus, Pencil, Trash2, X, Upload, FileText, Download, CheckCircle2, AlertCircle, CopyCheck, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";

const TXN_TYPES = [
  { value: "BUY", label: "Buy" },
  { value: "SELL", label: "Sell" },
  { value: "DIVIDEND", label: "Dividend" },
  { value: "SPLIT", label: "Stock Split" },
  { value: "BONUS", label: "Bonus Issue" },
];

const TYPE_PILL = {
  BUY: { bg: "var(--tint-accent-bg)", color: "var(--tint-accent-fg)" },
  SELL: { bg: "var(--tint-loss-bg)", color: "var(--tint-loss-fg)" },
  DIVIDEND: { bg: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)" },
  SPLIT: { bg: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)" },
  BONUS: { bg: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)" },
};

const emptyForm = {
  symbol: "",
  exchange: "NSE",
  company_name: "",
  txn_type: "BUY",
  quantity: "",
  price: "",
  amount: "",
  ratio: "",
  split_from: "1",
  split_to: "",
  date: format(new Date(), "yyyy-MM-dd"),
  notes: "",
};

export default function Transactions() {
  const { activeId } = usePortfolio();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detecting, setDetecting] = useState(false);

  const autoDetectExchange = async () => {
    const sym = (form.symbol || "").trim();
    if (!sym || isUSExchange(form.exchange)) return;
    setDetecting(true);
    try {
      const r = await api.get(`/prices/detect?symbol=${encodeURIComponent(sym)}`);
      const d = r.data?.detected;
      if (d && (d.exchange === "NASDAQ" || d.exchange === "NYSE")) {
        setForm((f) => ({ ...f, exchange: d.exchange, company_name: f.company_name || d.name || "" }));
        toast.success(`Detected ${sym} on ${d.exchange} — currency set to USD ($)`);
      }
    } catch { /* detection is best-effort */ } finally {
      setDetecting(false);
    }
  };
  const [submitting, setSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [fx, setFx] = useState(null);
  const fileInputRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/transactions");
      setRows(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (activeId) load(); }, [activeId]);
  useEffect(() => {
    let alive = true;
    api.get("/fx/usd-inr").then((r) => { if (alive) setFx(Number(r.data?.rate) || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // ---- CSV import ----
  const openImport = () => { setImportPreview(null); setImportFileName(""); setImportOpen(true); };

  const downloadTemplate = () => {
    const csv = [
      "Date,Type,Symbol,Exchange,Quantity,Price,Amount,Ratio",
      "2024-06-01,BUY,RELIANCE,NSE,10,2400,,",
      "2024-09-01,SELL,RELIANCE,NSE,4,2850,,",
      "2025-02-01,DIVIDEND,RELIANCE,NSE,,,250,",
      "2024-10-28,SPLIT,RELIANCE,NSE,,,,2",
      "2024-11-15,BONUS,TCS,NSE,,,,1",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "equitybox-import-template.csv";
    a.click(); URL.revokeObjectURL(url);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    setPreviewing(true);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/transactions/import/preview", fd);
      setImportPreview(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that CSV");
    } finally {
      setPreviewing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const commitImport = async () => {
    const okRows = (importPreview?.rows || []).filter((r) => r.status === "ok").map((r) => r.txn);
    if (okRows.length === 0) return;
    setImporting(true);
    try {
      const r = await api.post("/transactions/import/commit", { rows: okRows });
      const n = r.data?.imported || 0;
      const sk = r.data?.skipped || 0;
      toast.success(`Imported ${n} transaction${n === 1 ? "" : "s"}${sk ? ` · skipped ${sk} duplicate${sk === 1 ? "" : "s"}` : ""}`);
      setImportOpen(false);
      setImportPreview(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const revalidate = async (mapping) => {
    if (!importPreview) return null;
    setPreviewing(true);
    try {
      const r = await api.post("/transactions/import/revalidate", {
        headers: importPreview.headers, raw_rows: importPreview.raw_rows, mapping,
      });
      setImportPreview((p) => ({ ...p, mapping: r.data.mapping, rows: r.data.rows, counts: r.data.counts }));
      return r.data;
    } catch (err) {
      toast.error("Could not re-check with that mapping");
      return null;
    } finally {
      setPreviewing(false);
    }
  };

  const setFieldMap = (field, headerName) => {
    const mapping = { ...(importPreview.mapping || {}) };
    if (headerName === "__none__") delete mapping[field]; else mapping[field] = headerName;
    revalidate(mapping);
  };

  const applyPreset = async (broker) => {
    const preset = importPreview?.presets?.[broker] || {};
    const lower = (importPreview.headers || []).map((h) => (h || "").toLowerCase().trim());
    // Merge the preset over the current (auto-detected) mapping so fields the
    // preset doesn't specify keep their detected column.
    const mapping = { ...(importPreview.mapping || {}) };
    Object.entries(preset).forEach(([field, cands]) => {
      for (const c of cands) {
        const idx = lower.indexOf(c.toLowerCase().trim());
        if (idx >= 0) { mapping[field] = importPreview.headers[idx]; break; }
      }
    });
    const res = await revalidate(mapping);
    if (res && res.counts && res.counts.ok === 0 && res.counts.total > 0) {
      toast.warning(`${broker} preset couldn't match every column — adjust the mapping below.`);
    } else if (res) {
      toast.success(`Applied ${broker} column mapping`);
    }
  };

  const validateTxn = (t) => {
    const errs = [];
    if (!["BUY", "SELL", "DIVIDEND", "BONUS", "SPLIT"].includes(t.txn_type)) errs.push("Unknown transaction type");
    if (!t.symbol) errs.push("Missing symbol");
    if (!t.date || !/^\d{4}-\d{2}-\d{2}$/.test(t.date)) errs.push("Missing or invalid date (YYYY-MM-DD)");
    if (t.txn_type === "BUY" || t.txn_type === "SELL") {
      if (!(Number(t.quantity) > 0)) errs.push("Quantity must be > 0");
      if (!(Number(t.price) > 0)) errs.push("Price must be > 0");
    } else if (t.txn_type === "DIVIDEND") {
      if (!(Number(t.amount) > 0)) errs.push("Dividend amount must be > 0");
    } else if (t.txn_type === "BONUS") {
      if (!(Number(t.quantity) > 0) && !(Number(t.ratio) > 0)) errs.push("Bonus needs a quantity or ratio");
    } else if (t.txn_type === "SPLIT") {
      if (!(Number(t.ratio) > 0)) errs.push("Split needs a ratio");
    }
    return errs;
  };

  const editRow = (line, patch) => {
    setImportPreview((p) => {
      const rows = p.rows.map((r) => {
        if (r.line !== line) return r;
        const txn = { ...r.txn, ...patch };
        ["quantity", "price", "amount", "ratio"].forEach((k) => { if (k in patch) txn[k] = Number(patch[k]) || 0; });
        if ("symbol" in patch) txn.symbol = (patch.symbol || "").toUpperCase();
        const errors = validateTxn(txn);
        return { ...r, txn, errors, status: errors.length ? "error" : "ok" };
      });
      return { ...p, rows, counts: {
        total: rows.length,
        ok: rows.filter((r) => r.status === "ok").length,
        duplicate: rows.filter((r) => r.status === "duplicate").length,
        error: rows.filter((r) => r.status === "error").length,
      } };
    });
  };

  const exportCSV = () => {
    const header = "Date,Type,Symbol,Exchange,Quantity,Price,Amount,Ratio";
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = sortedRows.map((t) => [t.date || "", t.txn_type || "", t.symbol || "", t.exchange || "",
      t.quantity || "", t.price || "", t.amount || "", t.ratio && t.ratio !== 1 ? t.ratio : ""].map(esc).join(","));
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `equitybox-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const sortedRows = useMemo(() => [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || "")), [rows]);

  const [search, setSearch] = useState("");
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter(
      (r) => (r.symbol || "").toLowerCase().includes(q) || (r.company_name || "").toLowerCase().includes(q)
    );
  }, [sortedRows, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      symbol: row.symbol,
      exchange: row.exchange || "NSE",
      company_name: row.company_name || "",
      txn_type: row.txn_type,
      quantity: row.quantity?.toString() || "",
      price: row.price?.toString() || "",
      amount: row.amount?.toString() || "",
      ratio: row.ratio?.toString() || "",
      split_from: "1",
      split_to: row.txn_type === "SPLIT" && row.ratio ? row.ratio.toString() : "",
      date: row.date,
      notes: row.notes || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.symbol.trim()) { toast.error("Symbol is required"); return; }
    if (!form.date) { toast.error("Date is required"); return; }
    // Stock split: compute ratio = to / from
    let ratio = parseFloat(form.ratio) || 1;
    if (form.txn_type === "SPLIT") {
      const from = parseFloat(form.split_from);
      const to = parseFloat(form.split_to);
      if (!from || from <= 0 || !to || to <= 0) {
        toast.error("Enter valid From and To values for the split");
        return;
      }
      ratio = to / from;
      if (Math.abs(ratio - 1) < 1e-9) {
        toast.error("Split ratio cannot be 1 → 1 (no change)");
        return;
      }
    }
    const payload = {
      symbol: form.symbol.trim().toUpperCase(),
      exchange: form.exchange || "NSE",
      company_name: form.company_name || "",
      txn_type: form.txn_type,
      quantity: parseFloat(form.quantity) || 0,
      price: parseFloat(form.price) || 0,
      amount: parseFloat(form.amount) || 0,
      ratio,
      date: form.date,
      notes: form.notes || "",
    };
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/transactions/${editing.id}`, payload);
        toast.success("Transaction updated");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transaction added");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.txn_type} ${row.symbol}?`)) return;
    try {
      await api.delete(`/transactions/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const showsPrice = ["BUY", "SELL"].includes(form.txn_type);
  const showsAmount = form.txn_type === "DIVIDEND";
  const showsQty = ["BUY", "SELL", "BONUS"].includes(form.txn_type);
  const showsRatio = form.txn_type === "SPLIT";

  return (
    <Layout
      subtitle="Ledger"
      title="Transactions"
      actions={
        <div className="flex items-center gap-2">
          <Button data-testid="export-csv-button" variant="outline" onClick={exportCSV} disabled={rows.length === 0} className="rounded px-3 sm:px-4 py-2.5 text-sm">
            <Download className="w-4 h-4 sm:mr-2" strokeWidth={1.75} />
            <span className="hidden sm:inline">Export CSV</span>
          </Button>
          <Button data-testid="import-csv-button" variant="outline" onClick={openImport} className="rounded px-3 sm:px-4 py-2.5 text-sm">
            <Upload className="w-4 h-4 sm:mr-2" strokeWidth={1.75} />
            <span className="hidden sm:inline">Import CSV</span>
          </Button>
          <Button data-testid="add-transaction-button" onClick={openCreate} className="rounded px-4 sm:px-5 py-2.5 text-sm" style={{ background: "var(--btn-primary-bg)" }}>
            <Plus className="w-4 h-4 mr-1.5 sm:mr-2" strokeWidth={1.75} />
            <span className="hidden sm:inline">Add transaction</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      }
    >
      {sortedRows.length > 0 && (
        <div className="mb-4 relative max-w-md" data-testid="txn-search-wrap">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-muted)" }} strokeWidth={1.75} />
          <Input
            data-testid="txn-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by stock name or ticker…"
            className="rounded pl-9 pr-9"
          />
          {search && (
            <button
              data-testid="txn-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-white/10"
              style={{ color: "var(--text-muted)" }}
              aria-label="Clear search"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      <section className="surface-card overflow-hidden" data-testid="transactions-table">
        {loading ? (
          <div className="p-10 text-center label-overline">Loading…</div>
        ) : sortedRows.length === 0 ? (
          <div className="p-12 text-center">
            <p className="font-display text-xl mb-2">No transactions yet</p>
            <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>Start by adding your first BUY transaction.</p>
            <Button data-testid="empty-add-button" onClick={openCreate} className="rounded" style={{ background: "var(--btn-primary-bg)" }}>
              <Plus className="w-4 h-4 mr-2" /> Add transaction
            </Button>
          </div>
        ) : filteredRows.length === 0 ? (
          <div className="p-12 text-center" data-testid="txn-no-matches">
            <p className="font-display text-xl mb-2">No matching transactions</p>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Nothing matches “<span style={{ color: "var(--text-primary)" }}>{search}</span>”. Try a different stock name or ticker.
            </p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="md:hidden flex flex-col divide-y" style={{ borderColor: "var(--border-soft)" }}>
              {filteredRows.map((r) => (
                <TxnCard key={`${r.id}-card`} r={r} fx={fx} onEdit={openEdit} onDelete={remove} />
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-base)" }}>
                  <tr>
                    <Th>Date</Th>
                    <Th>Type</Th>
                    <Th>Symbol</Th>
                    <Th right>Qty</Th>
                    <Th right>Price</Th>
                    <Th right>Amount</Th>
                    <Th right>Ratio</Th>
                    <Th right>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.id} className="border-t" style={{ borderColor: "var(--border-soft)" }} data-testid={`txn-row-${r.id}`}>
                      <td className="py-4 px-6 font-mono text-xs">{r.date}</td>
                      <td className="py-4 px-6">
                        <span className="px-2.5 py-1 rounded text-xs font-medium" style={{ background: TYPE_PILL[r.txn_type]?.bg, color: TYPE_PILL[r.txn_type]?.color }}>
                          {r.txn_type}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="font-medium flex items-center gap-2">
                          {r.symbol}
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: r.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)", color: r.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)" }}>
                            {r.exchange || "NSE"}
                          </span>
                        </div>
                        {r.company_name && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{r.company_name}</div>}
                      </td>
                      <Td right mono>{["BUY","SELL"].includes(r.txn_type) ? fmtQty(r.quantity) : r.txn_type === "BONUS" ? `+${fmtQty(r.quantity)}` : "—"}</Td>
                      <Td right mono>{["BUY","SELL"].includes(r.txn_type) ? <Money val={r.price} exchange={r.exchange} fx={fx} /> : "—"}</Td>
                      <Td right mono>{r.txn_type === "DIVIDEND" ? <Money val={r.amount} exchange={r.exchange} fx={fx} /> : ["BUY","SELL"].includes(r.txn_type) ? <Money val={r.quantity * r.price} exchange={r.exchange} fx={fx} /> : "—"}</Td>
                      <Td right mono>{r.txn_type === "SPLIT" ? `1 → ${Number(r.ratio).toFixed(r.ratio % 1 === 0 ? 0 : 2).replace(/\.?0+$/, "")}` : "—"}</Td>
                      <td className="py-4 px-6 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button data-testid={`edit-${r.id}`} onClick={() => openEdit(r)} className="p-2 rounded-lg hover:bg-white/5">
                            <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                          </button>
                          <button data-testid={`delete-${r.id}`} onClick={() => remove(r)} className="p-2 rounded-lg hover:bg-white/5" style={{ color: "var(--accent-negative)" }}>
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="txn-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[21px] font-semibold tracking-[-0.015em]">
              {editing ? "Edit transaction" : "New transaction"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Type">
                <Select value={form.txn_type} onValueChange={(v) => setForm({ ...form, txn_type: v })}>
                  <SelectTrigger data-testid="form-type" className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TXN_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`type-${t.value}`}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Date">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" type="button" data-testid="form-date" className="w-full justify-start rounded-lg font-normal">
                      {form.date ? format(new Date(form.date), "dd MMM yyyy") : "Pick date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.date ? new Date(form.date) : new Date()}
                      onSelect={(d) => d && setForm({ ...form, date: format(d, "yyyy-MM-dd") })}
                    />
                  </PopoverContent>
                </Popover>
              </Field>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <Field label="Symbol">
                <Input data-testid="form-symbol" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} onBlur={autoDetectExchange} placeholder="RELIANCE / AAPL" className="rounded-lg" />
                {detecting && <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>Detecting exchange…</p>}
              </Field>
              <Field label="Exchange">
                <Select value={form.exchange} onValueChange={(v) => setForm({ ...form, exchange: v })}>
                  <SelectTrigger data-testid="form-exchange" className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NSE" data-testid="exchange-NSE">NSE</SelectItem>
                    <SelectItem value="BSE" data-testid="exchange-BSE">BSE</SelectItem>
                    <SelectItem value="NASDAQ" data-testid="exchange-NASDAQ">NASDAQ</SelectItem>
                    <SelectItem value="NYSE" data-testid="exchange-NYSE">NYSE</SelectItem>
                    <SelectItem value="NYSE AMERICAN" data-testid="exchange-NYSE-AMERICAN">NYSE American</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="col-span-2 sm:col-span-1">
                <Field label="Company (optional)">
                  <Input data-testid="form-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Reliance Industries" className="rounded-lg" />
                </Field>
              </div>
            </div>

            {showsQty && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label={form.txn_type === "BONUS" ? "Bonus shares received" : "Quantity"}>
                  <Input data-testid="form-quantity" type="number" step="any" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder={form.txn_type === "BONUS" ? "e.g. 100" : "10"} className="rounded-lg" />
                  {form.txn_type === "BONUS" && (
                    <p className="text-xs mt-1.5" style={{ color: "var(--text-muted)" }}>
                      Enter the number of free shares credited. Your average cost falls automatically.
                    </p>
                  )}
                </Field>
                {showsPrice && (
                  <Field label={`Price per share (${isUSExchange(form.exchange) ? "$" : "₹"})`}>
                    <Input data-testid="form-price" type="number" step="any" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="2500" className="rounded-lg" />
                  </Field>
                )}
              </div>
            )}

            {showsAmount && (
              <Field label={`Dividend amount (${isUSExchange(form.exchange) ? "$" : "₹"})`}>
                <Input data-testid="form-amount" type="number" step="any" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="500" className="rounded-lg" />
              </Field>
            )}

            {showsRatio && (
              <div>
                <Label className="label-overline mb-2 block">Split ratio</Label>
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                  <Input
                    data-testid="form-split-from"
                    type="number"
                    step="any"
                    min="0"
                    value={form.split_from}
                    onChange={(e) => setForm({ ...form, split_from: e.target.value })}
                    placeholder="From"
                    className="rounded-lg text-center"
                  />
                  <span className="font-display text-2xl" style={{ color: "var(--text-muted)" }}>→</span>
                  <Input
                    data-testid="form-split-to"
                    type="number"
                    step="any"
                    min="0"
                    value={form.split_to}
                    onChange={(e) => setForm({ ...form, split_to: e.target.value })}
                    placeholder="To"
                    className="rounded-lg text-center"
                  />
                </div>
                <p className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>
                  Each <b>{form.split_from || "1"}</b> share becomes <b>{form.split_to || "?"}</b> shares.
                  {form.split_from && form.split_to && parseFloat(form.split_from) > 0 && parseFloat(form.split_to) > 0 && (
                    <> Ratio <b>{(parseFloat(form.split_to) / parseFloat(form.split_from)).toFixed(4)}×</b> · quantity {parseFloat(form.split_to) > parseFloat(form.split_from) ? "multiplies" : "shrinks"}, avg cost adjusts inversely.</>
                  )}
                </p>
                <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
                  Examples: 1 → 2 (regular split), 1 → 5, 2 → 5 (odd split), 5 → 1 (reverse split).
                </p>
              </div>
            )}

            <Field label="Notes (optional)">
              <Input data-testid="form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Broker, exchange, etc." className="rounded-lg" />
            </Field>

            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded">
                <X className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button type="submit" data-testid="form-submit" disabled={submitting} className="rounded" style={{ background: "var(--btn-primary-bg)" }}>
                {submitting ? "Saving…" : editing ? "Save changes" : "Add transaction"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Import transactions from CSV</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Upload a CSV of your trade history. Use the EquityBox template, or drop in a Zerodha / Groww / Upstox
              export — we'll auto-detect the columns. Nothing is added until you confirm.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={onPickFile} className="hidden" data-testid="import-file-input" />
              <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={previewing} className="rounded" style={{ background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" }} data-testid="import-choose-file">
                <FileText className="w-4 h-4 mr-2" strokeWidth={1.75} />
                {previewing ? "Reading…" : "Choose CSV file"}
              </Button>
              <Button type="button" variant="outline" onClick={downloadTemplate} className="rounded" data-testid="import-download-template">
                <Download className="w-4 h-4 mr-2" strokeWidth={1.75} /> Download template
              </Button>
              {importFileName && <span className="text-xs font-mono" style={{ color: "var(--text-muted)" }}>{importFileName}</span>}
            </div>

            {importPreview && (
              <div className="space-y-3" data-testid="import-preview">
                {/* Broker presets */}
                <div className="flex flex-wrap items-center gap-2">
                  <span className="label-overline text-[10px]" style={{ color: "var(--text-muted)" }}>Broker preset</span>
                  {Object.keys(importPreview.presets || {}).map((b) => (
                    <button key={b} type="button" onClick={() => applyPreset(b)} disabled={previewing}
                      data-testid={`preset-${b.toLowerCase()}`}
                      className="text-xs px-2.5 py-1 rounded border transition-colors hover:bg-white/5 disabled:opacity-60"
                      style={{ borderColor: "var(--border-soft)", color: "var(--text-secondary)" }}>
                      {b}
                    </button>
                  ))}
                </div>

                {/* Column mapping */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="import-mapping">
                  {(importPreview.fields || []).map((f) => (
                    <div key={f}>
                      <label className="label-overline text-[9px] block mb-1" style={{ color: "var(--text-muted)" }}>{f.replace("txn_type", "type")}</label>
                      <Select value={importPreview.mapping?.[f] || "__none__"} onValueChange={(v) => setFieldMap(f, v)}>
                        <SelectTrigger className="h-8 text-xs rounded" data-testid={`map-${f}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— none —</SelectItem>
                          {(importPreview.headers || []).map((h, i) => <SelectItem key={i} value={h}>{h || `(col ${i + 1})`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded" style={{ background: "var(--tint-accent-bg)", color: "var(--tint-accent-fg)" }} data-testid="import-count-ok">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {importPreview.counts.ok} ready
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded" style={{ background: "var(--tint-neutral-bg)", color: "var(--tint-neutral-fg)" }} data-testid="import-count-duplicate">
                    <CopyCheck className="w-3.5 h-3.5" /> {importPreview.counts.duplicate} duplicate
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded" style={{ background: "var(--tint-loss-bg)", color: "var(--tint-loss-fg)" }} data-testid="import-count-error">
                    <AlertCircle className="w-3.5 h-3.5" /> {importPreview.counts.error} skipped
                  </span>
                </div>

                <div className="max-h-72 overflow-y-auto rounded border" style={{ borderColor: "var(--border-soft)" }}>
                  <table className="w-full text-xs">
                    <thead className="sticky top-0" style={{ background: "var(--bg-base)" }}>
                      <tr>
                        <th className="text-left py-2 px-3 label-overline">Row</th>
                        <th className="text-left py-2 px-3 label-overline">Detail</th>
                        <th className="text-right py-2 px-3 label-overline">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r) => (
                        <tr key={r.line} className="border-t align-top" style={{ borderColor: "var(--border-soft)" }} data-testid={`import-row-${r.line}`}>
                          <td className="py-2 px-3 font-mono" style={{ color: "var(--text-muted)" }}>{r.line}</td>
                          <td className="py-2 px-3">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <select value={r.txn.txn_type} onChange={(e) => editRow(r.line, { txn_type: e.target.value })}
                                data-testid={`edit-type-${r.line}`}
                                className="h-7 rounded px-1.5 text-xs bg-transparent border" style={{ borderColor: "var(--border-soft)" }}>
                                <option value="">type?</option>
                                {["BUY", "SELL", "DIVIDEND", "BONUS", "SPLIT"].map((tp) => <option key={tp} value={tp}>{tp}</option>)}
                              </select>
                              <input value={r.txn.symbol} onChange={(e) => editRow(r.line, { symbol: e.target.value })}
                                data-testid={`edit-symbol-${r.line}`} placeholder="SYMBOL" size={8}
                                className="h-7 rounded px-1.5 text-xs bg-transparent border w-20 font-mono uppercase" style={{ borderColor: "var(--border-soft)" }} />
                              <input value={r.txn.date} onChange={(e) => editRow(r.line, { date: e.target.value })}
                                data-testid={`edit-date-${r.line}`} placeholder="YYYY-MM-DD"
                                className="h-7 rounded px-1.5 text-xs bg-transparent border w-28 font-mono" style={{ borderColor: "var(--border-soft)" }} />
                              {(r.txn.txn_type === "BUY" || r.txn.txn_type === "SELL" || r.txn.txn_type === "BONUS") && (
                                <input value={r.txn.quantity || ""} onChange={(e) => editRow(r.line, { quantity: e.target.value })}
                                  data-testid={`edit-quantity-${r.line}`}
                                  placeholder="qty" className="h-7 rounded px-1.5 text-xs bg-transparent border w-16 font-mono" style={{ borderColor: "var(--border-soft)" }} />
                              )}
                              {(r.txn.txn_type === "BUY" || r.txn.txn_type === "SELL") && (
                                <input value={r.txn.price || ""} onChange={(e) => editRow(r.line, { price: e.target.value })}
                                  data-testid={`edit-price-${r.line}`}
                                  placeholder="price" className="h-7 rounded px-1.5 text-xs bg-transparent border w-20 font-mono" style={{ borderColor: "var(--border-soft)" }} />
                              )}
                              {r.txn.txn_type === "DIVIDEND" && (
                                <input value={r.txn.amount || ""} onChange={(e) => editRow(r.line, { amount: e.target.value })}
                                  data-testid={`edit-amount-${r.line}`}
                                  placeholder="amount" className="h-7 rounded px-1.5 text-xs bg-transparent border w-24 font-mono" style={{ borderColor: "var(--border-soft)" }} />
                              )}
                              {(r.txn.txn_type === "SPLIT" || r.txn.txn_type === "BONUS") && (
                                <input value={r.txn.ratio || ""} onChange={(e) => editRow(r.line, { ratio: e.target.value })}
                                  data-testid={`edit-ratio-${r.line}`}
                                  placeholder="ratio" className="h-7 rounded px-1.5 text-xs bg-transparent border w-16 font-mono" style={{ borderColor: "var(--border-soft)" }} />
                              )}
                            </div>
                            {r.errors?.length > 0 && (
                              <div className="mt-1" style={{ color: "var(--tint-loss-fg)" }}>{r.errors.join(" · ")}</div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right">
                            {r.status === "ok" && <span style={{ color: "var(--accent-positive)" }}>Ready</span>}
                            {r.status === "duplicate" && <span style={{ color: "var(--text-muted)" }}>Duplicate</span>}
                            {r.status === "error" && <span style={{ color: "var(--tint-loss-fg)" }}>Skip</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setImportOpen(false)} className="rounded">Cancel</Button>
            <Button
              type="button"
              onClick={commitImport}
              disabled={importing || !(importPreview?.counts?.ok > 0)}
              className="rounded"
              style={{ background: "var(--btn-primary-bg)", color: "var(--btn-primary-fg)" }}
              data-testid="import-commit-button"
            >
              {importing ? "Importing…" : `Import ${importPreview?.counts?.ok || 0} transaction${(importPreview?.counts?.ok || 0) === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-overline mb-2 block">{label}</Label>
      {children}
    </div>
  );
}

function TxnCard({ r, fx, onEdit, onDelete }) {
  const exchBadge = {
    background: r.exchange === "BSE" ? "var(--tint-neutral-bg)" : "var(--tint-accent-bg)",
    color: r.exchange === "BSE" ? "var(--tint-neutral-fg)" : "var(--tint-accent-fg)",
  };
  const usd = isUSExchange(r.exchange);
  const m = (v) => (usd ? fmtUSD(v) : fmtINR(v));
  const isBuySell = ["BUY", "SELL"].includes(r.txn_type);
  let primaryDetail = "";
  if (isBuySell) {
    primaryDetail = `${fmtQty(r.quantity)} @ ${m(r.price)}`;
  } else if (r.txn_type === "DIVIDEND") {
    primaryDetail = m(r.amount);
  } else if (r.txn_type === "BONUS") {
    primaryDetail = `+${fmtQty(r.quantity)} shares`;
  } else if (r.txn_type === "SPLIT") {
    const ratioStr = Number(r.ratio).toFixed(r.ratio % 1 === 0 ? 0 : 2).replace(/\.?0+$/, "");
    primaryDetail = `1 → ${ratioStr}`;
  }
  return (
    <div className="px-4 py-4" data-testid={`txn-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider"
              style={{ background: TYPE_PILL[r.txn_type]?.bg, color: TYPE_PILL[r.txn_type]?.color }}
            >
              {r.txn_type}
            </span>
            <span className="font-medium text-sm">{r.symbol}</span>
            <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded" style={exchBadge}>
              {r.exchange || "NSE"}
            </span>
          </div>
          {r.company_name && (
            <div className="text-[11px] mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>
              {r.company_name}
            </div>
          )}
          <div className="font-mono text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{r.date}</div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0 -mr-2">
          <button
            data-testid={`edit-${r.id}-mobile`}
            onClick={() => onEdit(r)}
            className="p-2 rounded-lg hover:bg-white/5"
            aria-label="Edit"
          >
            <Pencil className="w-4 h-4" strokeWidth={1.75} />
          </button>
          <button
            data-testid={`delete-${r.id}-mobile`}
            onClick={() => onDelete(r)}
            className="p-2 rounded-lg hover:bg-white/5"
            style={{ color: "var(--accent-negative)" }}
            aria-label="Delete"
          >
            <Trash2 className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>
      <div className="font-mono text-sm" data-testid={`txn-card-detail-${r.id}`}>
        {primaryDetail}
        {isBuySell && (
          <span className="ml-2" style={{ color: "var(--text-muted)" }}>
            = {m(r.quantity * r.price)}{usd && fx ? ` (${fmtINR(r.quantity * r.price * fx)})` : ""}
          </span>
        )}
      </div>
    </div>
  );
}

// Currency-aware money cell: US-stock figures shown in $ with the ₹ equivalent
// underneath (using the live FX rate); Indian figures shown in ₹.
function Money({ val, exchange, fx, showSign = false }) {
  if (isUSExchange(exchange)) {
    return (
      <>
        {fmtUSD(val, { showSign })}
        {fx ? (
          <div className="text-[10px] font-normal leading-tight" style={{ color: "var(--text-muted)" }}>
            {fmtINR((Number(val) || 0) * fx, { showSign })}
          </div>
        ) : null}
      </>
    );
  }
  return fmtINR(val, { showSign });
}

function Th({ children, right }) {
  return <th className={`label-overline py-3 px-6 ${right ? "text-right" : "text-left"}`} style={{ color: "var(--text-muted)", fontSize: "0.65rem" }}>{children}</th>;
}
function Td({ children, right, mono, className = "" }) {
  return <td className={`py-4 px-6 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;
}
